/**
 * Sélection du « représentant » d'une pièce dans la checklist Documents quand
 * plusieurs fichiers du même type sont déposés (recto + verso, doublons, ou
 * scans multi-documents contenant aussi une autre pièce).
 *
 * On retient le MEILLEUR scan : statut le plus avancé (`ok`), puis confiance la
 * plus haute, puis le plus récent. Sans ça, le plus récent gagnait — un verso
 * seul à 82 % pouvait masquer un scan complet à 97 % du même type et afficher
 * la pièce « à vérifier » alors qu'elle est valide (cas DC-39155 : permis
 * recto/verso + CNI répartis sur plusieurs scans).
 */

import type { CgDocType } from '../ocr/ocrResponseTypes';
import type { IngestionItem } from '../store/useFormCGIAStore';

/**
 * Rang d'un statut d'ingestion : une pièce réellement analysée (`ok`) prime sur
 * un upload/analyse en cours, qui prime sur un échec (`mismatch`/`unreadable`/
 * `error`).
 */
export const INGESTION_STATUS_RANK: Record<IngestionItem['status'], number> = {
  ok: 3,
  analyzing: 2,
  uploading: 2,
  queued: 1,
  mismatch: 0,
  unreadable: 0,
  error: 0,
};

/**
 * `true` si `candidate` est un meilleur représentant que `current` : statut le
 * plus avancé, puis confiance la plus haute, puis le plus récent.
 */
export const isBetterDocRowCandidate = (candidate: IngestionItem, current: IngestionItem): boolean => {
  const rankCandidate = INGESTION_STATUS_RANK[candidate.status] ?? 0;
  const rankCurrent = INGESTION_STATUS_RANK[current.status] ?? 0;
  if (rankCandidate !== rankCurrent) return rankCandidate > rankCurrent;
  const confCandidate = candidate.confidence ?? 0;
  const confCurrent = current.confidence ?? 0;
  if (confCandidate !== confCurrent) return confCandidate > confCurrent;
  return candidate.createdAt > current.createdAt;
};

/**
 * Réduit les fichiers déposés à UN représentant par type de pièce (le meilleur
 * scan, cf. `isBetterDocRowCandidate`). Les items sans type identifiable
 * (`unknown`) sont ignorés.
 *
 * Source unique de la notion de « représentant de slot », partagée par
 * `buildDocRows` (statut de la checklist) et `selectReceivedUploads` (sous-bloc
 * « Autres pièces reçues »). Tout item qui N'EST PAS le représentant de son slot
 * est une pièce reçue en plus (verso, doublon, 2ᵉ face) qui doit rester
 * VISIBLE : sans ça, un verso déposé en second fichier disparaissait de l'écran
 * et l'agence croyait qu'il manquait une pièce (retour Biwiz 2026-07).
 */
export const pickSlotRepresentatives = (
  ingestionItems: Record<string, IngestionItem>,
  /**
   * URL Firebase à FORCER comme représentant du slot `piece_identite` — utilisé
   * par le départage titulaire/co-titulaire : le slot « Pièce d'identité du
   * nouveau titulaire » doit montrer la CNI du TITULAIRE (matchée sur
   * cession/mandat), pas la 1re CNI arrivée. La 2ᵉ CNI (co-titulaire) devient
   * alors non-représentante et migre dans son sous-bloc dédié.
   */
  titulaireCniUrl?: string,
): Map<CgDocType, IngestionItem> => {
  const byDocType = new Map<CgDocType, IngestionItem>();
  const forced = titulaireCniUrl
    ? Object.values(ingestionItems).find((it) => it.firebaseUrl === titulaireCniUrl)
    : undefined;
  Object.values(ingestionItems).forEach((item) => {
    if (!item.docTypeDetected || item.docTypeDetected === 'unknown') return;
    // Slot piece_identite forcé sur la CNI du titulaire : on ignore les autres
    // CNI dans la boucle (elles deviennent non-représentantes → sous-blocs).
    if (item.docTypeDetected === 'piece_identite' && forced) return;
    const existing = byDocType.get(item.docTypeDetected);
    if (!existing || isBetterDocRowCandidate(item, existing)) {
      byDocType.set(item.docTypeDetected, item);
    }
  });
  if (forced) byDocType.set('piece_identite', forced);
  return byDocType;
};
